// DateSheet — the mobile render of every date field (mobile overhaul Phase 0
// §0.3.2; mockup §2.1 D). Every date on mobile goes through this: task due,
// lead follow-up, invoice dates, project dates, crew bookings, snooze. No
// raw <input type="date"> anywhere.
//
// A half sheet in three parts: a chip row of the common answers (Today,
// Tomorrow, +3 days, +1 week — callers override for snooze / follow-ups),
// the month grid at 40px cells, and a footer whose primary names the result
// it will commit ("Set · Thu 17 Sep", "Hide until Thu 17 Sep"). Nothing
// commits until that button is pressed, so a mis-tap on the grid is free.
//
// The grid maths are the desktop CalendarPanel's, imported rather than
// forked, so both renders always agree about what a month looks like.

import { useEffect, useState } from 'react';
import { melbourneToday } from '../../shared/dates';
import { Sheet, SheetButton } from './Sheet';
import { DP_DOW, addDays, formatWeekdayDate, monthGrid, shiftMonth } from '../lib/calendar';

/** A quick answer above the grid. `date: null` clears the field. */
export interface DateChip {
  label: string;
  date: string | null;
}

// en-AU ICU spells these fine, but the mockups set the month header in full
// Fraunces ("September 2026") while primitives' MONTHS is the 3-letter set.
const CAL_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Today · Tomorrow · +3 days · +1 week — the answer for most fields. */
export function defaultDateChips(today: string): DateChip[] {
  return [
    { label: 'Today', date: today },
    { label: 'Tomorrow', date: addDays(today, 1) },
    { label: '+3 days', date: addDays(today, 3) },
    { label: '+1 week', date: addDays(today, 7) },
  ];
}

export function DateSheet({
  open,
  title,
  context,
  value,
  onPick,
  onClose,
  allowClear,
  chips,
  verb,
  seed,
}: {
  open: boolean;
  title: string;
  /** Mono eyebrow under the title — "Bridge Creative · $1,200 · 215d overdue". */
  context?: string;
  value: string | null;
  onPick: (iso: string | null) => void;
  onClose: () => void;
  allowClear?: boolean;
  /** Override the quick chips; default Today · Tomorrow · +3 days · +1 week
      (+ Clear when allowClear && value). */
  chips?: DateChip[];
  /** Primary button verb, default "Set" → "Set · Thu 17 Sep". */
  verb?: string;
  /** Month an empty field opens on instead of today. */
  seed?: string | null;
}) {
  const today = melbourneToday();
  const openOn = value ?? seed ?? today;
  // `undefined` = untouched (the footer still offers the current value);
  // `null` = an explicit clear.
  const [pending, setPending] = useState<string | null | undefined>(undefined);
  const [viewY, setViewY] = useState(Number(openOn.slice(0, 4)));
  const [viewM, setViewM] = useState(Number(openOn.slice(5, 7)));

  // Re-seed each time the sheet opens — a stale pending date from the last
  // field would otherwise show up in the footer.
  useEffect(() => {
    if (!open) return;
    setPending(undefined);
    setViewY(Number(openOn.slice(0, 4)));
    setViewM(Number(openOn.slice(5, 7)));
    // openOn is derived from value/seed, which is what we mean to react to.
  }, [open, openOn]);

  if (!open) return null;

  // A caller's own clearing chip (follow-ups pass "Done" as null) only
  // counts when clearing is allowed for this field.
  const quick = (chips ?? defaultDateChips(today)).filter((c) => allowClear || c.date !== null);
  const row: DateChip[] = allowClear && value ? [...quick, { label: 'Clear', date: null }] : quick;

  const touched = pending !== undefined;
  const selected: string | null = touched ? pending : value;
  const onChip = row.some((c) => c.date === selected);
  // Nothing to commit: the field is empty and no chip or day was tapped.
  const empty = !touched && value === null;

  const jumpTo = (iso: string) => {
    setViewY(Number(iso.slice(0, 4)));
    setViewM(Number(iso.slice(5, 7)));
  };

  const monthShift = (delta: number) => {
    const next = shiftMonth(viewY, viewM, delta);
    setViewY(next.year);
    setViewM(next.month);
  };

  const commit = () => {
    if (selected === null && !allowClear) return;
    onPick(selected);
    onClose();
  };

  // A bare "Set" takes the middot; a caller's phrase reads as a sentence
  // ("Hide until Thu 17 Sep") — both spellings are in this file's header.
  const primaryLabel =
    selected === null
      ? 'Clear date'
      : verb
        ? `${verb} ${formatWeekdayDate(selected)}`
        : `Set · ${formatWeekdayDate(selected)}`;

  return (
    <Sheet
      open
      onClose={onClose}
      variant="half"
      title={title}
      context={context}
      rightAction={<SheetButton onClick={onClose}>Cancel</SheetButton>}
      className="sheet-date"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={empty} onClick={commit}>
            {empty ? (verb ?? 'Set') : primaryLabel}
          </button>
        </>
      }
    >
      <div className="chiprow">
        {row.map((c) => (
          <button
            type="button"
            key={c.label}
            className={`c${!empty && selected === c.date ? ' on' : ''}`}
            onClick={() => {
              setPending(c.date);
              if (c.date) jumpTo(c.date);
            }}
          >
            {c.label}
          </button>
        ))}
        <span className={`c static${empty || onChip ? '' : ' on'}`} aria-hidden="true">
          Pick date
        </span>
      </div>
      <div className="cal">
        <div className="cal-head">
          <span className="cal-month">
            {CAL_MONTHS[viewM - 1]} {viewY}
          </span>
          <span className="cal-nav">
            <button type="button" onClick={() => monthShift(-1)} aria-label="Previous month">
              ‹
            </button>
            <button type="button" onClick={() => monthShift(1)} aria-label="Next month">
              ›
            </button>
          </span>
        </div>
        <div className="cal-grid">
          {DP_DOW.map((d) => (
            <span className="cal-dow" key={d}>
              {d}
            </span>
          ))}
          {monthGrid(viewY, viewM).map((iso) => {
            const cls = [
              'cal-day',
              Number(iso.slice(5, 7)) !== viewM ? 'out' : '',
              iso === today ? 'today' : '',
              iso === selected ? 'sel' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                type="button"
                className={cls}
                key={iso}
                aria-pressed={iso === selected}
                onClick={() => setPending(iso)}
              >
                {Number(iso.slice(8, 10))}
              </button>
            );
          })}
        </div>
      </div>
    </Sheet>
  );
}
